'use client';

import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { LandTiers } from '../services/AggregateLand/AggregateLandService';
interface IRedirectData {
  redirect_url: string;
}

interface IPaymentSummary {
  payment_id: string;
  payment_status: string;
  pay_address: string;
  price_amount: number;
  price_currency: string;
  pay_amount: number;
  amount_received: number;
  pay_currency: string;
  order_id?: string;
  order_description?: string;
  payin_extra_id: string;
  ipn_callback_url: string;
  created_at: string;
  updated_at: string;
  purchase_id: string;
  smart_contract?: string;
  network: string;
  network_precision?: number;
  time_limit?: string;
  burning_percent?: number;
  expiration_estimate_date: string;
  is_fixed_rate: boolean;
  is_fee_paid_by_user: boolean;
  valid_until: string;
  redirectData: IRedirectData;
  type: string;
}

const InitialPaymentSummary = {
  payment_id: '',
  payment_status: '',
  pay_address: '',
  price_amount: 0,
  price_currency: '',
  pay_amount: 0,
  amount_received: 0,
  pay_currency: '',
  order_id: undefined,
  order_description: undefined,
  payin_extra_id: '',
  ipn_callback_url: '',
  created_at: '',
  updated_at: '',
  purchase_id: '',
  smart_contract: undefined,
  network: '',
  network_precision: undefined,
  time_limit: undefined,
  burning_percent: undefined,
  expiration_estimate_date: '',
  is_fixed_rate: false,
  is_fee_paid_by_user: false,
  valid_until: '',
  redirectData: {
    redirect_url: '',
  },
  type: '',
};

interface LandPurchaseContextProps {
  landType: string;
  totalEth: number;
  landQuantity: number;
  pricePerLand: number;
  purchaseSummary: IPaymentSummary;
  isPurchaseSuccessfully: boolean;
  updateLandQuantity: (value: number) => void;
  updateTotalEth: (value: number) => void;
  updatePricePerLand: (string: LandTiers.tier4 | LandTiers.tier3) => void;
  updateLandType: (string: string) => void;
  updatePurchaseSummary: (string: IPaymentSummary) => void;
  resetPurchaseSummary: () => void;
  updatePurchaseSuccessfully: (value: boolean) => void;
}
interface LandPurchaseProviderProps {
  children: ReactNode;
}

const initialContext: LandPurchaseContextProps = {
  landType: LandTiers.tier4,
  landQuantity: 0,
  totalEth: 0,
  pricePerLand: 0,
  purchaseSummary: InitialPaymentSummary,
  isPurchaseSuccessfully: false,
  updateLandQuantity: () => { },
  updateTotalEth: () => { },
  updatePricePerLand: () => { },
  updateLandType: () => { },
  updatePurchaseSummary: () => { },
  resetPurchaseSummary: () => { },
  updatePurchaseSuccessfully: () => { },
};

const MAX_QUANTITY = 255;

export const LandPurchaseContext = createContext<LandPurchaseContextProps>(initialContext);

export const LandPurchaseProvider = ({ children }: LandPurchaseProviderProps) => {
  const [totalEth, setTotalEth] = useState<number>(0);
  const [pricePerLand, setPricePerLand] = useState<number>(0.2);
  const [landQuantity, setLandQuantity] = useState<number>(1);
  const [landType, setLandType] = useState<string>(LandTiers.tier4);
  const [purchaseSummary, setPurchaseSummary] = useState<IPaymentSummary>(InitialPaymentSummary);
  const [isPurchaseSuccessfully, setPurchaseSuccessfully] = useState<boolean>(false);

  // useEffect(() => {
  //   const ethNum = (Number(pricePerLand) * landQuantity).toFixed(2);
  //   setTotalEth(+ethNum);
  // }, [pricePerLand, landQuantity]);

  const updateLandQuantity = (value: number) => {
    if (value > 0 && value <= MAX_QUANTITY) {
      setLandQuantity(value);
    } else {
      setLandQuantity(1);
    }
  };
  const updateTotalEth = (value: number) => {
    if (value >= 0) {
      setTotalEth(value);
    } else {
      setTotalEth(0);
    }
  };
  const updatePricePerLand = (value: LandTiers.tier4 | LandTiers.tier3) => {
    if (value === LandTiers.tier4) setPricePerLand(0.2);
    if (value === LandTiers.tier3) setPricePerLand(0.3);
  };
  const updateLandType = (value: string) => {
    if (value === LandTiers.tier4) {
      setPricePerLand(0.2);
      setLandType(value);
    }
    if (value === LandTiers.tier3) {
      setPricePerLand(0.3);
      setLandType(value);
    }
  };
  const updatePurchaseSummary = (value: IPaymentSummary) => {
    setPurchaseSummary(value);
  };
  const resetPurchaseSummary = () => {
    setPurchaseSummary(InitialPaymentSummary);
  };
  const updatePurchaseSuccessfully = (value: boolean) => {
    setPurchaseSuccessfully(value);
  };

  return (
    <LandPurchaseContext.Provider
      value={{
        landQuantity,
        totalEth,
        pricePerLand,
        landType,
        purchaseSummary,
        isPurchaseSuccessfully,
        updateLandQuantity,
        updateTotalEth,
        updateLandType,
        updatePricePerLand,
        updatePurchaseSummary,
        updatePurchaseSuccessfully,
        resetPurchaseSummary,
      }}
    >
      {children}
    </LandPurchaseContext.Provider>
  );
};
